import Icon from "@/Components/Icon/Icon";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import {
  useAddCartItem,
  useDecreaseCartItem,
  useRemoveCartItem,
  useUpdateCartItemByIndex,
} from "@/shared/hooks/useCartHook";
import { cn } from "@/shared/utils/cn";
import { CartItem } from "@/shared/utils/interfaces";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

const KioskQuantityStepper = ({
  quantity,
  onDecrease,
  onIncrease,
}: {
  quantity: number;
  onDecrease: () => void;
  onIncrease: () => void;
}) => {
  const { t } = useTranslation("", { keyPrefix: "kioskPage" });

  return (
    <div
      className="flex shrink-0 items-center gap-3 rounded-full border-2 border-primary/25 bg-white p-2 shadow-md"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={t("decreaseQuantity")}
        onClick={onDecrease}
        className={cn(
          "flex h-30 w-30 items-center justify-center rounded-full",
          "border-2 border-primary bg-white text-primary",
          "transition-transform duration-100 active:scale-95",
          "touch-manipulation select-none"
        )}
      >
        <Icon variants="minus" className="h-12 w-12 shrink-0" />
      </button>

      <span
        className="min-w-20 px-2 text-center text-[4rem] font-black leading-none tabular-nums text-text"
        aria-live="polite"
        aria-atomic="true"
      >
        {quantity}
      </span>

      <button
        type="button"
        aria-label={t("increaseQuantity")}
        onClick={onIncrease}
        className={cn(
          "flex h-30 w-30 items-center justify-center rounded-full",
          "bg-primary text-white shadow-sm",
          "transition-transform duration-100 active:scale-95",
          "touch-manipulation select-none"
        )}
      >
        <Icon variants="plus" className="h-12 w-12 shrink-0 fill-white" />
      </button>
    </div>
  );
};

const KioskCartItem = ({
  cartItem,
  index,
  showCounter = true,
  showEdit = true,
  mb = "2rem",
  onlyName = false,
  imgWidth = "10rem",
  textCentered = false,
}: {
  cartItem: CartItem;
  index: number;
  showCounter?: boolean;
  showEdit?: boolean;
  mb?: string;
  onlyName?: boolean;
  imgWidth?: string;
  textCentered?: boolean;
}) => {
  const [meta] = useModalMeta();
  const id = meta?.id;
  const setModal = useSetModal();
  const history = useHistory();
  const { t } = useTranslation("", { keyPrefix: "posOrders" });
  const removeCartItem = useRemoveCartItem();
  const addCartItem = useAddCartItem();
  const decreaseCartItem = useDecreaseCartItem();
  const updateCartItemByIndex = useUpdateCartItemByIndex();

  const description = useMemo(() => {
    return `${cartItem?.sub_content?.map((item) => item?.name).join(", ")}`;
  }, [cartItem?.sub_content]);

  const openNoteModal = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setModal("kioskCartItemNote", {
      index,
      cartItem,
      id,
    });
  };

  const removeNote = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateCartItemByIndex(index, { ...cartItem, note: "" });
  };

  return (
    <div className="whitespace-[nowrap]" style={{ marginBottom: mb }}>
      <div
        className="flex gap-8 rounded-2xl bg-(--background_color) p-[1.5rem_2rem] text-[3rem] text-text"
        style={{ fontWeight: 900 }}
      >
        {cartItem?.image_cloudinary_id && (
          <div style={{ height: imgWidth, flex: `0 0 ${imgWidth}` }}>
            <img
              src={cartItem?.image_cloudinary_id}
              alt=""
              className="mx-auto h-auto object-contain"
              style={{ width: imgWidth }}
            />
          </div>
        )}
        <div
          className="min-w-0 flex-1"
          style={{
            display: textCentered ? "flex" : "block",
            alignItems: textCentered ? "center" : "unset",
          }}
        >
          <div
            className="flex items-center gap-6"
            style={{ minHeight: onlyName ? imgWidth : undefined }}
          >
            <div className="my-auto">{cartItem?.name}</div>
            {showEdit && cartItem.sub_content.length !== 0 && (
              <button
                type="button"
                aria-label="Edit item"
                onClick={() =>
                  history.push(
                    `/kiosk/menu/${id}/${cartItem.parent_id}/${cartItem.item_id}?index=${index}`
                  )
                }
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/80 touch-manipulation active:scale-95"
              >
                <Icon className="h-8 w-8 fill-primary" variants="edit" />
              </button>
            )}
            {showEdit && (
              <button
                type="button"
                aria-label="Remove item"
                onClick={() => removeCartItem(index)}
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/80 touch-manipulation active:scale-95"
              >
                <Icon className="h-8 w-8 fill-error" variants="trash" />
              </button>
            )}
          </div>
          {!onlyName && (
            <div className="text-[1.75rem] font-normal">{description}</div>
          )}
          {showEdit && !onlyName && !cartItem.note && (
            <button
              type="button"
              onClick={openNoteModal}
              className="mt-3 flex items-center gap-3 text-[1.75rem] font-normal text-primary touch-manipulation active:scale-95"
            >
              <Icon className="h-7 w-7 fill-primary" variants="comment" />
              {t("addNote")}
            </button>
          )}
          {showEdit && !onlyName && cartItem.note && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                aria-label="Remove note"
                onClick={removeNote}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80 touch-manipulation active:scale-95"
              >
                <Icon className="h-6 w-6 fill-error" variants="times" />
              </button>
              <button
                type="button"
                onClick={openNoteModal}
                className="min-w-0 text-left text-[1.75rem] font-normal italic text-primary touch-manipulation active:opacity-70"
              >
                {cartItem.note}
              </button>
            </div>
          )}
        </div>
        {showCounter && (
          <KioskQuantityStepper
            quantity={cartItem.quantity}
            onDecrease={() => decreaseCartItem(cartItem)}
            onIncrease={() => addCartItem({ ...cartItem, quantity: 1 })}
          />
        )}
      </div>
    </div>
  );
};

export default KioskCartItem;
